import Promise from 'bluebird';

let sharedDebugId = 0;

/**
 * Augments the behaviour of the passed in promise to help reduce flickerness
 * of the UI by:
 * - delaying the completion of the promise for it to last at least the
 *   specified amount of time;
 * - delaying calling, or not calling at all, the "on"/"off" progress callbacks,
 *   if the promise finishes before the "on" delay elapses.
 * @summary "Deflickers" a promise
 * @param {Object} options - the options
 * @param {Number} [options.onDelay=500] - the delay in milliseconds before
 * the "on" progress callback is called,
 * should the promise not finish by that time
 * @param {Number} [options.offDelay=700] - the delay in milliseconds before
 * the "off" progress callback is called, after the "on" callback was called
 * @param {Function} options.on - the "on" progress callback, which is called
 * if process runs longer than the "on" delay. The callback must return the "off"
 * progress callback. The "off" progress callback will be called after the process
 * finishes and the "off" delay elapses.
 * @param {Any} [options.logDebug] - If not undefined, enables trace output of the
 * component using window.console.log-like function. Each message from a specific instance
 * of the deflickered promise will have a uniqe ID logged with every message traced.
 * @param {Promise} promise - the promise to augment the behaviour of
 * @returns {Promise} a new promise with the augmented behaviour
 */
export function deflickerPromise(options, promise) {
  const {
    onDelay = 500, offDelay = 700, on, logDebug = () => { /* NOP */ },
  } = options;
  let resolveOffDelayPromise;
  const offDelayPromise = new Promise((resolve) => {
    resolveOffDelayPromise = resolve;
  });
  let off = null;
  let onTimerFired = false;
  const debugId = sharedDebugId++; // eslint-disable-line 

  logDebug(`Deflicker [${debugId}]: Enter. onDelay = ${onDelay}, offDelay = ${offDelay}`);

  const setup = () => {
    // Start the "on" timer
    const onTimerId = setTimeout(() => {
      logDebug(`Deflicker [${debugId}]: On timer`);

      onTimerFired = true;
      off = on();

      // Start the "off" timer
      setTimeout(() => {
        logDebug(`Deflicker [${debugId}]: Off timer`);
        resolveOffDelayPromise();
      }, offDelay);
    }, onDelay);

    return () => {
      logDebug(`Deflicker [${debugId}]: Process finished. onTimerFired = ${onTimerFired}`);

      // If the process finishes before the "on" timer fires,
      // cancel it and resolve the "off" delay promise
      if (!onTimerFired) {
        clearTimeout(onTimerId);
        resolveOffDelayPromise();
      }
    };
  };

  return promise
    .finally(setup()) // set up and start the "on" timer
    .finally(() => offDelayPromise)
    .finally(() => {
      logDebug(`Deflicker [${debugId}]: Overall wait finished. onTimerFired = ${onTimerFired}`);

      if (off) {
        off();
      }
    });
}
